/**
 * AtBatForm Component
 *
 * The entry form for one at-bat, shared by the add and edit paths.
 *
 * The result picker is the whole hot path — logging a plate appearance mid-game
 * should be one choice. Event time defaults to now and lives behind a disclosure,
 * so back-filling is possible without putting a datetime picker in the way of the
 * common case.
 */

import { useState } from "react";
import { Button } from "../../../../../components/Button";
import {
  AT_BAT_RESULT_DISPLAY,
  AT_BAT_RESULT_GROUPS,
} from "../../../../../constants/atBatResults";
import {
  currentUtcOffsetMinutes,
  fromDateTimeLocalValue,
  toDateTimeLocalValue,
} from "../../../../../utils/atBatLog";
import type { AtBat, AtBatResult } from "../../../../../types/at-bats";
import "./AtBatForm.css";

export interface AtBatFormValues {
  result: AtBatResult;
  details: string | null;
  occurredAt: string;
  occurredAtOffsetMinutes: number;
  sequenceNumber?: number;
}

interface AtBatFormProps {
  /** When present the form edits this entry; otherwise it logs a new one. */
  atBat?: AtBat;
  submitting: boolean;
  onSubmit: (values: AtBatFormValues) => void;
  onCancel: () => void;
}

const MAX_DETAILS_LENGTH = 500;

/** `yyyy-MM-ddTHH:mm` for the browser's current local time. */
const nowAsDateTimeLocal = (): string =>
  toDateTimeLocalValue(new Date().toISOString(), currentUtcOffsetMinutes());

export function AtBatForm({
  atBat,
  submitting,
  onSubmit,
  onCancel,
}: AtBatFormProps) {
  const isEdit = Boolean(atBat);

  // An existing entry keeps its original ballpark offset: the input shows the
  // wall clock the coach saw, so re-deriving the offset from this browser would
  // shift the instant when a road game is reviewed from another timezone.
  const offsetMinutes = atBat?.occurredAtOffsetMinutes ?? currentUtcOffsetMinutes();

  const [result, setResult] = useState<AtBatResult | "">(atBat?.result ?? "");
  const [details, setDetails] = useState(atBat?.details ?? "");
  const [occurredAtLocal, setOccurredAtLocal] = useState(() =>
    atBat
      ? toDateTimeLocalValue(atBat.occurredAt, atBat.occurredAtOffsetMinutes)
      : nowAsDateTimeLocal(),
  );
  // Editing always shows the time — a coach opening an existing entry is usually
  // there to correct exactly that.
  const [timeExpanded, setTimeExpanded] = useState(isEdit);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!result) {
      setValidationError("Pick a result for this at-bat.");
      return;
    }

    const occurredAt = fromDateTimeLocalValue(occurredAtLocal, offsetMinutes);
    if (!occurredAt) {
      setValidationError("Enter a valid date and time for this at-bat.");
      return;
    }

    setValidationError(null);
    onSubmit({
      result,
      details: details.trim() ? details.trim() : null,
      occurredAt,
      occurredAtOffsetMinutes: offsetMinutes,
      // Sequence is left to the server on create (next slot in that game); an
      // edit keeps whatever order the entry already had.
      sequenceNumber: atBat?.sequenceNumber,
    });
  };

  return (
    <form className="at-bat-form" onSubmit={handleSubmit}>
      <div className="at-bat-form__field">
        <label className="at-bat-form__label" htmlFor="at-bat-result">
          Result
        </label>
        <select
          id="at-bat-result"
          className="at-bat-form__select"
          value={result}
          onChange={(event) => setResult(event.target.value as AtBatResult)}
          disabled={submitting}
          required
        >
          <option value="">Select a result…</option>
          {AT_BAT_RESULT_GROUPS.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.results.map((code) => (
                <option key={code} value={code}>
                  {AT_BAT_RESULT_DISPLAY[code].label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <div className="at-bat-form__field">
        <label className="at-bat-form__label" htmlFor="at-bat-details">
          Details <span className="at-bat-form__optional">(optional)</span>
        </label>
        <input
          id="at-bat-details"
          className="at-bat-form__input"
          type="text"
          value={details}
          maxLength={MAX_DETAILS_LENGTH}
          placeholder="Count, pitch seen, contact quality…"
          onChange={(event) => setDetails(event.target.value)}
          disabled={submitting}
        />
      </div>

      {timeExpanded ? (
        <div className="at-bat-form__field">
          <label className="at-bat-form__label" htmlFor="at-bat-occurred-at">
            When it happened
          </label>
          <input
            id="at-bat-occurred-at"
            className="at-bat-form__input"
            type="datetime-local"
            value={occurredAtLocal}
            onChange={(event) => setOccurredAtLocal(event.target.value)}
            disabled={submitting}
          />
          <p className="at-bat-form__hint">
            Defaults to now. Change it to log a game you already watched.
          </p>
        </div>
      ) : (
        <button
          type="button"
          className="at-bat-form__disclosure"
          onClick={() => setTimeExpanded(true)}
          disabled={submitting}
        >
          Logging an earlier at-bat? Set the time
        </button>
      )}

      {validationError && (
        <p className="at-bat-form__error" role="alert">
          {validationError}
        </p>
      )}

      <div className="at-bat-form__actions">
        <Button variant="secondary" type="button" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={submitting}>
          {submitting ? "Saving…" : isEdit ? "Save changes" : "Log at-bat"}
        </Button>
      </div>
    </form>
  );
}
